import {kebabCase} from 'lodash-es';
import type {ReactNode} from 'react';
import {useEffect, useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {Badge, Button, PermissionsProvider} from '@/components';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import {agreementService} from '@/services/agreementService';
import {fileService, kycBucketKey} from '@/services/fileService';
import type {
  BusinessAgreementDocumentView,
  BusinessAgreementView,
} from '@/types';
import {BusinessAgreementPath, BusinessAgreementSource} from '@/types';
import * as formatDate from '@/utils/date';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import UploadAgreementModal from './UploadAgreementModal';

type UploadKind = 'custom' | 'final';

const SOURCE_LABELS: Record<string, string> = {
  [BusinessAgreementSource.DocuSign]: 'Signed in the portal',
  [BusinessAgreementSource.BusinessUpload]: 'Uploaded by the business',
  [BusinessAgreementSource.AdminUpload]: 'Uploaded by AFX',
  [BusinessAgreementSource.Legacy]: 'From a live access request',
};

const UPLOAD_COPY: Record<UploadKind, {title: string; description: string}> = {
  custom: {
    title: 'Upload custom agreement',
    description:
      'The negotiated agreement for this business to sign. It appears on their Pricing page to download.',
  },
  final: {
    title: 'Upload final agreement',
    description:
      'The agreement in force for this business. It replaces the current one, which moves to Earlier agreements.',
  },
};

const pathLabel = (agreement: BusinessAgreementView): string => {
  if (agreement.path === BusinessAgreementPath.Negotiated) {
    return 'Negotiating with Sales';
  }
  if (agreement.path === BusinessAgreementPath.Standard) {
    return 'Standard agreement';
  }
  return 'Not chosen yet';
};

const Row = ({
  label,
  value,
  dataCy,
}: {
  label: string;
  value: ReactNode;
  dataCy?: string;
}) => (
  <div
    data-cy={dataCy}
    className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900"
  >
    <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
      {label}
    </p>
    <div className="flex-[0.6] text-sm font-medium text-gray-700 dark:text-gray-100">
      {value ?? '-'}
    </div>
  </div>
);

type PickDocument = (
  agreement: BusinessAgreementView,
) => BusinessAgreementDocumentView | undefined;

const smallButton = 'text-xs px-3 py-1 h-8';

// Upload sits on the row it fills, so the admin never picks a type: Upload
// while the row is empty, Replace once it holds a document.
const DocumentRow = ({
  label,
  document,
  onView,
  onUpload,
  dataCy,
}: {
  label: string;
  document?: BusinessAgreementDocumentView;
  onView: () => void;
  onUpload?: () => void;
  dataCy?: string;
}) => (
  <Row
    label={label}
    dataCy={dataCy}
    value={
      <div className="flex flex-wrap items-center gap-3">
        <span>
          {document
            ? `${SOURCE_LABELS[document.source] ?? document.source}, on ${formatDate.DDMonYYYY(document.at)}`
            : 'None'}
        </span>
        <div className="flex items-center gap-2">
          {document && (
            <Button
              variant="outline"
              colorScheme="cyan"
              className={smallButton}
              onClick={onView}
            >
              View PDF
            </Button>
          )}
          {onUpload && (
            <PermissionsProvider
              permission={AfriexAdminPermission.BusinessAgreementUpdate}
            >
              <Button
                variant="outline"
                colorScheme="cyan"
                className={smallButton}
                dataCy={dataCy ? `${dataCy}-upload` : undefined}
                onClick={onUpload}
              >
                {document ? 'Replace' : 'Upload'}
              </Button>
            </PermissionsProvider>
          )}
        </div>
      </div>
    }
  />
);

type BusinessAgreementTabProps = {
  businessId: string;
};

/**
 * The business's agreement with AFX: how it was reached, the custom agreement
 * shared for a negotiating business, the copy the business signed, and the
 * final agreement in force, with every earlier document kept below.
 */
const BusinessAgreementTab = ({businessId}: BusinessAgreementTabProps) => {
  const [agreement, setAgreement] = useState<BusinessAgreementView | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upload, setUpload] = useState<UploadKind | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const canView = hasAdminPermission(
    localStorage.getItem('admin-role') ?? undefined,
    AfriexAdminPermission.BusinessAgreementRead,
  );

  useEffect(() => {
    if (!businessId || !canView) {
      return undefined;
    }
    let cancelled = false;
    agreementService
      .get(businessId)
      .then((view) => {
        if (!cancelled) {
          setAgreement(view);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(getErrorMessage(e, 'Could not load the agreement.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, canView]);

  // Links expire five minutes after they are made, so a fresh one is fetched
  // on every click rather than reusing the one the tab loaded with.
  const viewDocument = async (pick: PickDocument) => {
    // Opened now, inside the click, so the browser does not block it after the wait.
    const popup = window.open('', '_blank');
    if (popup) {
      popup.opener = null;
    }
    try {
      const latest = await agreementService.get(businessId);
      setAgreement(latest);
      const url = pick(latest)?.url;
      if (!url) {
        throw new Error('The agreement is no longer available.');
      }
      if (popup) {
        popup.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      popup?.close();
      showErrorMessage(getErrorMessage(e, 'Could not open the agreement.'));
    }
  };

  const view = (pick: PickDocument) => () => {
    viewDocument(pick).catch(logRejection('Failed to open business agreement'));
  };

  const submitUpload = async (file: File) => {
    if (!upload) {
      return;
    }
    setIsUploading(true);
    try {
      const key = `live-access/${businessId}/agreement/${kebabCase(
        `${upload} agreement`,
      )}-${Date.now()}.pdf`;
      await fileService.uploadFile(key, kycBucketKey, businessId, file);
      setAgreement(
        upload === 'custom'
          ? await agreementService.uploadCustom(businessId, key)
          : await agreementService.replaceFinal(businessId, key),
      );
      showSuccessMessage('Agreement uploaded');
      setUpload(null);
    } catch (e) {
      showErrorMessage(getErrorMessage(e, 'Error uploading the agreement'));
    } finally {
      setIsUploading(false);
    }
  };

  if (!canView) {
    return (
      <p className="py-10 text-center text-gray-500 dark:text-gray-400">
        You do not have access to this business&apos;s agreement.
      </p>
    );
  }
  if (isLoading) {
    return <TableSkeleton />;
  }
  if (error || !agreement) {
    return (
      <p className="py-10 text-center text-gray-500 dark:text-gray-400">
        {error ?? 'Could not load the agreement.'}
      </p>
    );
  }

  const history = (agreement.history ?? [])
    .map((document, index) => ({document, index}))
    .reverse();

  return (
    <div
      data-cy="business-agreement"
      className="bg-white dark:bg-transparent rounded-lg w-full px-5 py-5"
    >
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl">Agreement</h2>
        <Badge
          dataCy="business-agreement-status"
          label={agreement.final ? 'In place' : 'Pending'}
          colorScheme={agreement.final ? 'successful' : 'pending'}
        />
      </div>

      <Row label="Path" value={pathLabel(agreement)} />
      {agreement.pathChosenAt && (
        <Row
          label="Chosen On"
          value={formatDate.DDMonYYYY(agreement.pathChosenAt)}
        />
      )}
      {agreement.msa && (
        <Row
          label="Portal signing"
          value={
            agreement.msa.signedAt
              ? `${agreement.msa.status}, on ${formatDate.DDMonYYYY(agreement.msa.signedAt)}`
              : agreement.msa.status
          }
        />
      )}
      {agreement.path === BusinessAgreementPath.Negotiated && (
        <>
          <DocumentRow
            label="Custom agreement"
            document={agreement.customAgreement}
            onView={view((latest) => latest.customAgreement)}
            onUpload={() => setUpload('custom')}
          />
          {/* A copy the business sends may still be under discussion; only an
              admin's final agreement makes it signed. */}
          <DocumentRow
            label={
              agreement.final
                ? 'Signed by the business'
                : 'Uploaded by the business'
            }
            document={agreement.signedUpload}
            onView={view((latest) => latest.signedUpload)}
          />
        </>
      )}
      <DocumentRow
        dataCy="business-agreement-final"
        label="Final agreement"
        document={agreement.final}
        onView={view((latest) => latest.final)}
        onUpload={() => setUpload('final')}
      />

      <div className="mt-6" data-cy="business-agreement-history">
        <h4 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
          EARLIER AGREEMENTS
        </h4>
        <hr className="mb-3 dark:border-dark-borderMid" />
        {history.length ? (
          // The history is append-only, so a document keeps its index across
          // refetches.
          history.map(({document, index}) => (
            <DocumentRow
              key={index}
              label={formatDate.DDMonYYYY(document.at)}
              document={document}
              onView={view((latest) => latest.history?.[index])}
            />
          ))
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No earlier agreements.
          </p>
        )}
      </div>

      <UploadAgreementModal
        isOpen={upload !== null}
        isLoading={isUploading}
        title={UPLOAD_COPY[upload ?? 'final'].title}
        description={UPLOAD_COPY[upload ?? 'final'].description}
        onClose={() => setUpload(null)}
        onSubmit={(file) => {
          submitUpload(file).catch(
            logRejection('Failed to upload business agreement'),
          );
        }}
      />
    </div>
  );
};

export default BusinessAgreementTab;
